import { useState, forwardRef } from "react";
import { Spinner } from "react-bootstrap";
import { apiCall } from "../../../services/api-service.js";
import moment from "moment";
import { ReactComponent as ModalCloseIcon } from "../../../Assets/svg/close-modal.svg";
import ticketIcon from "../../../Assets/svg/ticket_icon.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DateInput = forwardRef(function DateInput({ value, onClick, placeholder }, ref) {
  return (
    <input
      ref={ref}
      value={value}
      onClick={onClick}
      readOnly
      className="form-control w-100"
      style={{
        height: "48px",
        borderRadius: "12px",
        border: "1px solid #6929E2",
        padding: "12px 16px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        color: "#212129",
        fontFamily: "Satoshi",
      }}
      placeholder={placeholder}
    />
  );
});

const MODAL_OVERLAY = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const MODAL_CONTENT = {
  backgroundColor: "#ffffff",
  padding: "24px",
  borderRadius: "16px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.15)",
  width: "520px",
  maxHeight: "90vh",
  overflowY: "auto",
};

const STATUS_COLORS = {
  Open: "#4CAF50",
  "In Progress": "#4CAF50",
  Hold: "#FFC107",
};

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  const parsed = moment(dateStr, ["DD MMM YYYY", "DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm", "YYYY-MM-DD HH:mm:ss", "DD/MM/YYYY"], true);
  if (parsed.isValid()) return parsed;
  const fallback = moment(dateStr);
  return fallback.isValid() ? fallback : null;
};

const ExpectedDateUpdateModal = ({ ticketDetail, onSuccess, onHide }) => {
  const initialMoment = parseDateSafe(ticketDetail?.date);
  const [expectedDate, setExpectedDate] = useState(initialMoment ? initialMoment.format("YYYY-MM-DDTHH:mm") : "");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [validations, setValidations] = useState({
    expected_date: "",
    remarks: "",
  });

  const handleDateChange = (date) => {
    if (date) {
      setExpectedDate(moment(date).format("YYYY-MM-DDTHH:mm"));
      setValidations((prev) => ({ ...prev, expected_date: "" }));
    }
  };

  const handleSubmit = async () => {
    const newValidations = { expected_date: "", remarks: "" };

    if (!expectedDate) {
      newValidations.expected_date = "Expected date is required";
    }
    if (!remarks.trim()) {
      newValidations.remarks = "Remarks is required";
    }

    if (newValidations.expected_date || newValidations.remarks) {
      setValidations(newValidations);
      return;
    }

    if (loading) return;
    setLoading(true);

    const response = await apiCall("put", `ticket/expected_date_update/${ticketDetail.id}`, {
      expected_date: expectedDate,
      remarks: remarks,
    });

    if (response?.status === 200 && response?.data?.success === true) {
      onSuccess?.();
    } else if (response?.status === 400 && response?.data?.error) {
      const tempValidations = { expected_date: "", remarks: "" };
      Object.entries(response.data.error).forEach(([key, value]) => {
        if (tempValidations[key] !== undefined) {
          tempValidations[key] = Array.isArray(value) ? value[0] : value;
        }
      });
      setValidations(tempValidations);
    }
    setLoading(false);
  };

  const parsedDate = expectedDate ? (parseDateSafe(expectedDate)?.toDate() ?? null) : null;

  return (
    <div style={MODAL_OVERLAY}>
      <div style={MODAL_CONTENT}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <span
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "#212129",
              fontFamily: "Satoshi",
            }}
          >
            Edit Expected Date
          </span>
          <div
            onClick={onHide}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid #E5E5E5",
            }}
          >
            <ModalCloseIcon />
          </div>
        </div>

        {/* Ticket Info Card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px 16px",
            borderRadius: "12px",
            background: "#F7F8F9",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "8px",
              background: "#ffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img src={ticketIcon} alt="ticket" style={{ width: "18px", height: "18px" }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "600",
                color: "#212129",
                fontFamily: "Satoshi",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {ticketDetail?.code} - {ticketDetail?.title}
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "2px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#8790A1",
                  fontFamily: "Satoshi",
                }}
              >
                {ticketDetail?.component || ticketDetail?.project}
              </span>
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: STATUS_COLORS[ticketDetail?.status] || "#9E9E9E",
                }}
              ></span>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: STATUS_COLORS[ticketDetail?.status] || "#9E9E9E",
                  fontFamily: "Satoshi",
                }}
              >
                {ticketDetail?.status}
              </span>
            </div>
          </div>
        </div>

        {/* Expected Date Field */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#212129",
              fontFamily: "Satoshi",
              marginBottom: "8px",
              display: "block",
            }}
          >
            Expected date <span style={{ color: "#FF4444" }}>*</span>
          </label>
          <DatePicker selected={parsedDate} onChange={handleDateChange} showTimeInput timeInputLabel="Enter Time:" dateFormat="dd-MM-yyyy hh:mm aa" minDate={new Date()} placeholderText="dd-mm-yyyy hh:mm am/pm" wrapperClassName="w-100" popperClassName="expected-date-popper" portalId="portal-root" popperPlacement="bottom-start" popperProps={{ strategy: "fixed" }} customInput={<DateInput />} />
          {validations.expected_date && (
            <span
              style={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#FF4444",
                marginTop: "4px",
                display: "block",
              }}
            >
              {validations.expected_date}
            </span>
          )}
        </div>

        {/* Remarks Field */}
        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#212129",
              fontFamily: "Satoshi",
              marginBottom: "8px",
              display: "block",
            }}
          >
            Add Remarks <span style={{ color: "#FF4444" }}>*</span>
          </label>
          <textarea
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value);
              setValidations((prev) => ({ ...prev, remarks: "" }));
            }}
            placeholder="Enter here..."
            rows={4}
            style={{
              width: "100%",
              borderRadius: "12px",
              border: "1px solid #E5E5E5",
              padding: "12px 16px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#212129",
              fontFamily: "Satoshi",
              resize: "none",
              outline: "none",
            }}
          />
          {validations.remarks && (
            <span
              style={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#FF4444",
                marginTop: "4px",
                display: "block",
              }}
            >
              {validations.remarks}
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <div
            onClick={onHide}
            style={{
              cursor: "pointer",
              padding: "12px 32px",
              borderRadius: "100px",
              border: "0.6px solid #E5E5E5",
              backgroundColor: "#FFFFFF",
              fontSize: "14px",
              fontWeight: "700",
              color: "#212129",
              fontFamily: "Satoshi",
            }}
          >
            Cancel
          </div>
          <div
            onClick={handleSubmit}
            style={{
              cursor: loading ? "not-allowed" : "pointer",
              padding: "12px 32px",
              borderRadius: "100px",
              backgroundColor: "#6929E2",
              fontSize: "14px",
              fontWeight: "700",
              color: "#FFFFFF",
              fontFamily: "Satoshi",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? <Spinner animation="border" size="sm" variant="light" /> : "Submit"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpectedDateUpdateModal;
